/*=============================================== SkeletonPage ===============================================*/

import { Link } from "react-router-dom"
import { Skeleton, SkeletonCard, Text } from "tsx-library-julseb"

import { ComponentsLayout } from "components"
import type { ItemType, PropsItemType } from "components"

import { commonProps, radiusObject, borderObject } from "data"

const SkeletonPage = () => {
    const items: ItemType[] = [
        {
            title: "Pulse",
            content: (
                <SkeletonCard
                    padding="s"
                    border={{
                        width: 1,
                    }}
                    borderRadius="m"
                    flexDirection="column"
                    gap="s"
                >
                    <Skeleton
                        width={200}
                        height={150}
                        borderRadius="l"
                        animation="pulse"
                    />
                    <Skeleton
                        width="100%"
                        height={24}
                        borderRadius="s"
                        animation="pulse"
                    />
                    <Skeleton
                        width="90%"
                        height={24}
                        borderRadius="s"
                        animation="pulse"
                    />
                </SkeletonCard>
            ),
            code: '<SkeletonCard padding="s" border={{ width: 1 }} borderRadius="m" flexDirection="column" gap="s">\n    <Skeleton width={200} height={150} borderRadius="l" animation="pulse" />\n    <Skeleton width="100%" height={24} borderRadius="s" animation="pulse" />\n    <Skeleton width="90%" height={24} borderRadius="s" animation="pulse" />\n</SkeletonCard>',
        },
        {
            title: "Shine",
            content: (
                <SkeletonCard
                    padding="s"
                    border={{
                        width: 1,
                    }}
                    borderRadius="m"
                    flexDirection="column"
                    gap="s"
                >
                    <Skeleton
                        width={200}
                        height={150}
                        borderRadius="l"
                        animation="shine"
                    />
                    <Skeleton
                        width="100%"
                        height={24}
                        borderRadius="s"
                        animation="shine"
                    />
                    <Skeleton
                        width="90%"
                        height={24}
                        borderRadius="s"
                        animation="shine"
                    />
                </SkeletonCard>
            ),
            code: '<SkeletonCard padding="s" border={{ width: 1 }} borderRadius="m" flexDirection="column" gap="s">\n    <Skeleton width={200} height={150} borderRadius="l" animation="shine" />\n    <Skeleton width="100%" height={24} borderRadius="s" animation="shine" />\n    <Skeleton width="90%" height={24} borderRadius="s" animation="shine" />\n</SkeletonCard>',
        },
        {
            title: "Shine outside",
            content: (
                <SkeletonCard
                    padding="s"
                    border={{
                        width: 1,
                    }}
                    borderRadius="m"
                    flexDirection="column"
                    gap="s"
                    isShiny
                >
                    <Skeleton width={200} height={150} borderRadius="l" />
                    <Skeleton width="100%" height={24} borderRadius="s" />
                    <Skeleton width="90%" height={24} borderRadius="s" />
                </SkeletonCard>
            ),
            code: '<SkeletonCard padding="s" border={{ width: 1 }} borderRadius="m" flexDirection="column" gap="s" isShiny>\n    <Skeleton width={200} height={150} borderRadius="l" />\n    <Skeleton width="100%" height={24} borderRadius="s" />\n    <Skeleton width="90%" height={24} borderRadius="s" />\n</SkeletonCard>',
        },
    ]

    const propsItems: PropsItemType[] = [
        {
            title: (
                <>
                    <code>Skeleton</code> component
                </>
            ),
            subtitle: "as",
            intro: "Changes the HTML tag",
            type: "string",
            possibleString: "Any HTML tag",
            default: "div",
        },
        {
            subtitle: "width",
            type: "string or number",
        },
        {
            subtitle: "height",
            type: "string or number",
        },
        {
            subtitle: "maxWidth",
            type: "string or number",
        },
        {
            subtitle: "backgroundColor",
            type: "string",
            possibleString: commonProps.allColors,
            default: "background",
        },
        {
            subtitle: "flex",
            intro: (
                <>
                    If the container is a <code>flexbox</code>.
                </>
            ),
            type: "string or number",
        },
        {
            subtitle: "flexGrow",
            intro: (
                <>
                    If the container is a <code>flexbox</code>.
                </>
            ),
            type: "string or number",
        },
        {
            subtitle: "animation",
            type: "string",
            possible: ["pulse", "shine"],
        },
        ...radiusObject("small"),
        "separator",
        {
            title: (
                <>
                    <code>Shine</code> component
                </>
            ),
            subtitle: "as",
            intro: "Changes the HTML tag",
            type: "string",
            possibleString: "Any HTML tag",
            default: "span",
        },
        "separator",
        {
            title: (
                <>
                    <code>SkeletonCard</code> component
                </>
            ),
            componentIntro: (
                <Text>
                    Here, we are extending the{" "}
                    <Link to="/layouts/flexbox">Flexbox</Link> component.
                </Text>
            ),
            subtitle: "as",
            intro: "Changes the HTML tag",
            type: "string",
            possibleString: "Any HTML tag",
            default: "div",
        },
        {
            subtitle: "width",
            type: "string or number",
        },
        {
            subtitle: "height",
            type: "string or number",
        },
        {
            subtitle: "maxWidth",
            type: "string or number",
        },
        {
            subtitle: "backgroundColor",
            type: "string",
            possibleString: commonProps.allColors,
            default: "background",
        },
        {
            subtitle: "isShiny",
            type: "boolean",
        },
        ...borderObject("small"),
        ...radiusObject("small"),
    ]

    return <ComponentsLayout items={items} propsItems={propsItems} />
}

export default SkeletonPage
